import './offers-tab.scss';

import classNames from 'classnames';
import { useMemo, useState } from 'react';

import type { TLeasingOrderResponse } from '@/components/App/Leasing/types';
import { Button, OfferCard } from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES } from '@/constants';
import { ReloadOutlined } from '@/media/antd-icons';
import { formatPrice } from '@/services/formatter';
import { getUserFullName } from '@/services/helpers/strings';
import { useAppDispatch, useAppSelector } from '@/store';

import { DraftOffer } from '../../../entities';
import { ApplicationManagement } from '../../entities';
import { refreshOrderData } from '../../store';
import { HistoryModal } from '../../ui';
import useAcceptOffer from './hooks/useAcceptOffer';

const statusOrder = ['financed', 'approved', 'on_review', 'declined'];
const selectedStatuses = ['financed', 'approved'];

const OffersTab: React.FC = () => {
  const dispatch = useAppDispatch();

  const { request } = useAppSelector((state) => state.llpLeasingOrder);
  const { handleAcceptOffer, acceptLoading } = useAcceptOffer();

  const [settingsModal, setSettingsModal] = useState('');
  const [historyModal, setHistoryModal] = useState('');

  const isHasAccepted = false;

  const isHasFinancedOffer: boolean = useMemo(() => {
    if (!request) return false;
    return request.responses.some((offer) => offer.status.code === 'financed');
  }, [request]);

  const offers = useMemo(() => {
    if (!request) return [];
    return request.responses
      .filter((item) => item.status.code !== 'not_sent')
      .sort((a, b) => {
        const indexA = statusOrder.indexOf(a.status.code);
        const indexB = statusOrder.indexOf(b.status.code);
        const normalizedA = indexA === -1 ? statusOrder.length : indexA;
        const normalizedB = indexB === -1 ? statusOrder.length : indexB;
        return normalizedA - normalizedB;
      });
  }, [request?.responses]);

  const notSendedOffers = useMemo(() => {
    return (
      request?.responses.filter((item) => item.status.code === 'not_sent') || []
    );
  }, [request?.responses]);

  const isHaveFinanced =
    offers.find((item) => item.status.code === 'financed') !== undefined;

  const offerHistory = request?.responses.find(
    (item) => item.uuid === historyModal
  );

  const updateResponses = async () => {
    dispatch(refreshOrderData());
  };

  const getHeaderActions = (
    offer: TLeasingOrderResponse,
    isInHeader: boolean
  ): React.ReactNode[] => {
    const actions: React.ReactNode[] = [];
    const isAcceptButton =
      offer.status.code === 'approved' || offer.status.code === 'on_review';
    if (isAcceptButton && !isHasAccepted) {
      actions.push(
        <Button
          className={classNames({ 'offer-body__hidden': isInHeader })}
          key={offer.uuid + 'accept'}
          size="small"
          variant="filled-red"
          onClick={() => setSettingsModal(offer.uuid)}
          disabled={isHaveFinanced || offer.status.code !== 'approved'}
        >
          Управление
        </Button>
      );
    }

    if (offer.status.code === 'financed' || offer.status.code === 'on_review') {
      actions.push(
        <Button
          key={offer.uuid + 'history'}
          onClick={() => setHistoryModal(offer.uuid)}
          size="small"
          variant="outlined"
          className={classNames({ 'offer-body__hidden': isInHeader })}
        >
          История изменений
        </Button>
      );
    }

    return actions;
  };

  return (
    <div className="offers-tab">
      <div className="offer-step__header">
        <div className="offer-step__title-wrapper">
          <h4 className="offer-step__title">
            Предложения от лизинговых компаний
          </h4>
        </div>
        <div className="offer-step__actions">
          {!isHasFinancedOffer && (
            <Button
              className="offer-step__action"
              size="small"
              onClick={() => updateResponses()}
              isReversed
            >
              <p className="offer-step__button-text">Обновить</p>
              <ReloadOutlined />
            </Button>
          )}
        </div>
      </div>
      <div className="offers-tab__content">
        {offers.map((offer) => {
          const bankIcon =
            offer.leasingCompany.icon ||
            BANKS_ICONS_NAMES[
              offer.leasingCompany.name as keyof typeof BANKS_ICONS_NAMES
            ]?.icon;
          const isFinanced = offer.status.code === 'financed';
          const isOfferBody = offer.approvedAmount !== null;
          return (
            <OfferCard
              key={offer.uuid}
              status={
                selectedStatuses.includes(offer.status.code)
                  ? 'success'
                  : offer.status.code === 'on_review'
                    ? 'warning'
                    : 'error'
              }
              statusText={offer.status.name}
              isSelected={isFinanced}
            >
              <OfferCard.Header
                icon={bankIcon}
                title={offer.leasingCompany.name}
                headerActions={getHeaderActions(offer, true)}
                defaultMobileView={false}
              />
              {isOfferBody && (
                <OfferCard.Body
                  footerActions={getHeaderActions(offer, false)}
                  products={[
                    {
                      title: 'Сумма займа',
                      description: `${formatPrice(
                        offer.approvedAmount || request?.totalAmount || 0
                      )} ₸`
                    },
                    {
                      title: 'Первоначальный взнос',
                      description: `${formatPrice(
                        offer.initialPayment || request?.initialPayment || 0
                      )} ₸`
                    }
                  ]}
                />
              )}
            </OfferCard>
          );
        })}

        <div className="offers-tab__content">
          {notSendedOffers.map((offer) => (
            <DraftOffer
              key={offer.uuid}
              img={offer.leasingCompany.icon}
              name={offer.leasingCompany.name}
              status={{
                text: offer.status.name,
                code: offer.status.code
              }}
              onSend={() => handleAcceptOffer(offer.uuid)}
              sendLoading={acceptLoading.includes(offer.uuid)}
              isDisabled={isHaveFinanced}
              isAcceptButton
              isHasAccepted={isHasAccepted}
              uuid={offer.uuid}
            />
          ))}

          <ApplicationManagement
            offerUuid={settingsModal}
            isOpened={settingsModal !== ''}
            onClose={() => setSettingsModal('')}
          />

          <HistoryModal
            isOpened={historyModal !== ''}
            onClose={() => setHistoryModal('')}
            bankLogo={offerHistory?.leasingCompany.icon || ''}
            subtitle={offerHistory?.leasingCompany.name || ''}
            historyList={
              offerHistory?.history?.map((item) => ({
                comment: item.comment,
                date: item.createdAt,
                text: getUserFullName(item.user),
                status: { code: item.toStatus.code, text: item.toStatus.name }
              })) || []
            }
          />
        </div>
      </div>
    </div>
  );
};

export default OffersTab;
